import { SenderAddress } from "@/types/SenderAddress";
import { apiDelete, apiPut, apiGet, apiPost } from "../_apiShim";

const BASE_PATH = "/api/v1/sender-addresses";
const TEMPLATE_BASE_PATH = "/api/v1/template/sender-address";
const DEFAULT_FIELDS = "senderAddressId,emailDomainId,eventId,templateId,email,primary,alias";

// Create a sender address
export async function createSenderAddress(
  eventId: string,
  emailDomainId: string,
  email: string,
  alias?: string
): Promise<string> {
  return await apiPost<string>(
    BASE_PATH,
    {
      eventId,
      emailDomainId,
      email,
      ...(alias ? { alias } : {}),
    },
    {
      "Cache-Control": "no-store",
    }
  );
}

// Update a sender address
export async function updateSenderAddress(
  senderAddressId: string,
  eventId: string,
  alias: string | null
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}/${senderAddressId}`,
    {
      eventId,
      alias,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Get sender address by ID
export async function getSenderAddressById(
  senderAddressId: string,
  eventId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress> {
  return await apiGet<SenderAddress>(
    `${BASE_PATH}/${senderAddressId}?eventId=${eventId}&fields=${encodeURIComponent(
      fields
    )}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Set sender address as primary
export async function setPrimarySenderAddress(
  senderAddressId: string,
  eventId: string
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}/${senderAddressId}/primary`,
    {
      eventId,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete a sender address
export async function deleteSenderAddress(
  senderAddressId: string,
  eventId: string
): Promise<boolean> {
  return await apiDelete<boolean>(
    `${BASE_PATH}/${senderAddressId}?eventId=${eventId}`,
    {
      "Cache-Control": "no-store",
    }
  );
}

// Bulk delete sender addresses
export async function bulkDeleteSenderAddresses(
  senderAddressIds: string[],
  eventId: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}/bulk-delete`,
    { senderAddressIds, eventId },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Get sender addresses by event ID
export async function getSenderAddressesByEvent(
  eventId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress[]> {
  return await apiGet<SenderAddress[]>(
    `${BASE_PATH}/event/${eventId}?fields=${encodeURIComponent(fields)}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Get primary sender address for event
export async function getPrimarySenderAddressForEvent(
  eventId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress> {
  return await apiGet<SenderAddress>(
    `${BASE_PATH}/event/${eventId}/primary?fields=${encodeURIComponent(
      fields
    )}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// ── Template-scoped sender address functions ──

// Create a template sender address
export async function createTemplateSenderAddress(
  templateId: string,
  emailDomainId: string,
  email: string,
  alias?: string
): Promise<string> {
  return await apiPost<string>(
    `${TEMPLATE_BASE_PATH}/${templateId}`,
    {
      templateId,
      emailDomainId,
      email,
      ...(alias ? { alias } : {}),
    },
    {
      "Cache-Control": "no-store",
    }
  );
}

// Get sender addresses by template ID
export async function getSenderAddressesByTemplate(
  templateId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress[]> {
  return await apiGet<SenderAddress[]>(
    `${TEMPLATE_BASE_PATH}/${templateId}?fields=${encodeURIComponent(fields)}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Get template sender address by ID
export async function getTemplateSenderAddressById(
  senderAddressId: string,
  templateId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress> {
  return await apiGet<SenderAddress>(
    `${TEMPLATE_BASE_PATH}/${templateId}/${senderAddressId}?fields=${encodeURIComponent(fields)}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Get primary sender address for template
export async function getTemplatePrimarySenderAddress(
  templateId: string,
  fields: string = DEFAULT_FIELDS
): Promise<SenderAddress> {
  return await apiGet<SenderAddress>(
    `${TEMPLATE_BASE_PATH}/${templateId}/primary?fields=${encodeURIComponent(fields)}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

// Update template sender address
export async function updateTemplateSenderAddress(
  senderAddressId: string,
  templateId: string,
  alias: string | null
): Promise<void> {
  await apiPut<void>(
    `${TEMPLATE_BASE_PATH}/${templateId}/${senderAddressId}`,
    {
      templateId,
      alias,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Set primary sender address for template
export async function setTemplatePrimarySenderAddress(
  senderAddressId: string,
  templateId: string
): Promise<void> {
  await apiPut<void>(
    `${TEMPLATE_BASE_PATH}/${templateId}/${senderAddressId}/primary`,
    {
      templateId,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete template sender address
export async function deleteTemplateSenderAddress(
  senderAddressId: string,
  templateId: string
): Promise<boolean> {
  return await apiDelete<boolean>(
    `${TEMPLATE_BASE_PATH}/${templateId}/${senderAddressId}`,
    {
      "Cache-Control": "no-store",
    }
  );
}